import type { PtoPprTkDraft } from '@shared/api';
import { Banner, DataTable, Muted, TableWrap, Tag, fmtDateTime } from '../kfo/ui';
import { FindingItem } from './DocParts';
import {
  SEVERITY_ORDER,
  blockingFindings,
  groupFindings,
  reviewBadge,
  sectionLabelByKey,
} from './model';
import { SubTitle } from './ui';

const SEVERITY_TITLE: Record<(typeof SEVERITY_ORDER)[number], string> = {
  critical: 'Критично',
  major: 'Существенно',
  minor: 'Незначительно',
};

/**
 * Вкладка «Аудит»: журнал агента-аудитора — круги «аудит → исправление» и
 * замечания последнего круга. Блокирующие (critical, major) мешают утверждению;
 * `fixable=false` — ИИ сам не исправит, нужны данные или решение инженера.
 */
export function AuditTab({
  draft,
  onJump,
}: {
  draft: PtoPprTkDraft;
  onJump: (sectionKey: string) => void;
}) {
  const review = draft.review;
  const badge = reviewBadge(review);
  const findings = groupFindings(review?.findings ?? []);
  const blocking = blockingFindings(review).length;

  return (
    <div>
      <Muted>
        {badge && <Tag $tone={badge.tone}>{badge.text}</Tag>}
        {review?.reviewed_at ? ` · проверен ${fmtDateTime(review.reviewed_at)}` : ''}
        {findings.length > 0
          ? ` · замечаний ${findings.length}, из них блокирующих ${blocking}`
          : ''}
      </Muted>
      {review?.summary && <Banner $tone="info">{review.summary}</Banner>}
      {review?.error && <Banner $tone="danger">Сбой аудитора: {review.error}</Banner>}

      <SubTitle>Круги проверки</SubTitle>
      {(review?.rounds ?? []).length === 0 ? (
        <Muted>Аудит ещё не проводился.</Muted>
      ) : (
        <TableWrap $maxHeight="none">
          <DataTable>
            <thead>
              <tr>
                <th className="num">Круг</th>
                <th>Вердикт</th>
                <th className="num">Блокирующих / всего</th>
                <th>Исправлены по замечаниям</th>
                <th>Время</th>
              </tr>
            </thead>
            <tbody>
              {review.rounds.map((r, i) => (
                <tr key={`${r.round}-${i}`} data-round={r.round}>
                  <td className="num">{r.round}</td>
                  <td>
                    {r.verdict === 'approved' ? (
                      <Tag $tone="success">утверждено</Tag>
                    ) : (
                      <Tag $tone="warning">нужны исправления</Tag>
                    )}
                  </td>
                  <td className="num">
                    {r.blocking} / {r.total}
                  </td>
                  <td className="wrap">
                    {r.fixed_sections.length === 0
                      ? '—'
                      : r.fixed_sections.map((k) => sectionLabelByKey(draft, k)).join('; ')}
                  </td>
                  <td className="nowrap">{fmtDateTime(r.at)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </TableWrap>
      )}

      <SubTitle>Замечания последнего круга</SubTitle>
      {findings.length === 0 ? (
        <Muted>Замечаний нет.</Muted>
      ) : (
        SEVERITY_ORDER.map((sev) => {
          const list = findings.filter((g) => g.finding.severity === sev);
          if (list.length === 0) return null;
          return (
            <div key={sev} data-severity={sev}>
              <Muted>
                <b>{SEVERITY_TITLE[sev]}</b> · {list.length}
              </Muted>
              {list.map((g, i) => (
                <FindingItem
                  key={i}
                  finding={g.finding}
                  sectionKeys={g.sectionKeys}
                  draft={draft}
                  onJump={onJump}
                />
              ))}
            </div>
          );
        })
      )}
    </div>
  );
}
